import * as Yup from 'yup';
import { rntrcExpDateSchema, rntrcIssueDateSchema, toApiDate } from 'v3/utils/rntrcValidation';

export const REJECT_REASON_REQUIRED = 'Informe o motivo da reprovação';
export const REJECT_REASON_MAX = 'O motivo deve ter no máximo 1000 caracteres';
export const EMISSION_START_DATE_REQUIRED = 'Informe a data de início da emissão de contra CT-e';

export const rejectSchema = Yup.object({
  reason: Yup.string().trim().required(REJECT_REASON_REQUIRED).max(1000, REJECT_REASON_MAX),
});

export const emissionStartDateSchema = Yup.object({
  emissionStartDate: Yup.string()
    .required(EMISSION_START_DATE_REQUIRED)
    .test('valid-date', EMISSION_START_DATE_REQUIRED, (value) => !!toApiDate(value)),
});

const schema = Yup.object().shape({
  // Dados do usuário
  userName: Yup.string().required('Informe o nome completo').typeError('Informe o nome completo'),
  userDocument: Yup.string()
    .required('Informe o CPF do usuário')
    .typeError('Informe o CPF do usuário'),
  userEmail: Yup.string()
    .email('Informe um e-mail válido')
    .required('Informe o e-mail do usuário')
    .typeError('Informe o e-mail do usuário'),
  userPhone: Yup.string()
    .required('Informe o telefone do usuário')
    .typeError('Informe o telefone do usuário'),

  // Dados da transportadora
  autonomous: Yup.object()
    .nullable()
    .required('Informe se é autônomo')
    .typeError('Informe se é autônomo'),
  shippingDocument: Yup.string().required('Informe o CPF/CNPJ').typeError('Informe o CPF/CNPJ'),
  shippingPis: Yup.string().when('autonomous', {
    is: (val) => val?.value === true,
    then: (schema) => schema.required('Informe o PIS').typeError('Informe o PIS'),
    otherwise: (schema) => schema.nullable(),
  }),
  shippingName: Yup.string().required('Informe a razão social').typeError('Informe a razão social'),
  shippingStateRegistration: Yup.string().when('autonomous', {
    is: (val) => val?.value === false,
    then: (schema) =>
      schema.required('Informe a inscrição estadual').typeError('Informe a inscrição estadual'),
    otherwise: (schema) => schema.nullable(),
  }),
  shippingPhone: Yup.string()
    .required('Informe o telefone da transportadora')
    .typeError('Informe o telefone da transportadora'),
  shippingEmail: Yup.string()
    .email('Informe um e-mail válido')
    .required('Informe o e-mail da transportadora')
    .typeError('Informe o e-mail da transportadora'),
  shippingType: Yup.object()
    .nullable()
    .required('Informe o tipo da transportadora')
    .typeError('Informe o tipo da transportadora'),

  // Endereço
  country: Yup.object().nullable().required('Informe o país').typeError('Informe o país'),
  address: Yup.string().required('Informe o logradouro').typeError('Informe o logradouro'),
  addressNumberless: Yup.boolean(),
  addressNumber: Yup.string()
    .nullable()
    .when('addressNumberless', {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required('Informe o número').typeError('Informe o número'),
    }),
  addressNeighborhood: Yup.string().required('Informe o bairro').typeError('Informe o bairro'),
  addressZipCode: Yup.string().nullable().required('Informe o CEP').typeError('Informe o CEP'),
  addressCity: Yup.object().nullable().required('Informe a cidade').typeError('Informe a cidade'),
  addressProvince: Yup.object()
    .nullable()
    .required('Informe o estado')
    .typeError('Informe o estado'),
  addressComplement: Yup.string().nullable(),

  // ANTT / RNTRC
  anttNumber: Yup.string().required('Informe o número RNTRC').typeError('Informe o número RNTRC'),
  anttIssueDate: rntrcIssueDateSchema({
    required: true,
    requiredMessage: 'Informe a data de emissão',
  }),
  anttExpDate: rntrcExpDateSchema('anttIssueDate'),
  anttType: Yup.object()
    .nullable()
    .required('Informe o tipo ANTT')
    .typeError('Informe o tipo ANTT'),
  rntrcOwn: Yup.object().nullable().required('Informe o RNTRC').typeError('Informe o RNTRC'),
  anttTransporterDocument: Yup.string().when('rntrcOwn', {
    is: (val) => val?.value === false,
    then: (schema) =>
      schema
        .required('Informe o CPF/CNPJ do transportador')
        .typeError('Informe o CPF/CNPJ do transportador'),
    otherwise: (schema) => schema.nullable(),
  }),
});

export async function validateForm(data, setErrors) {
  const errorList = {};
  try {
    await schema.validate(data, { abortEarly: false });
    return true;
  } catch (err) {
    err.inner.forEach((error) => {
      errorList[error.path] = error?.message;
    });
    return false;
  } finally {
    setErrors(errorList);
  }
}
